// src/admin/pages/AdminSubscriptions.jsx
import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";

const STATUS_CONFIG = {
  active:   { color: "#16a34a", bg: "#dcfce7", label: "Active" },
  pending:  { color: "#d97706", bg: "#fef3c7", label: "Pending" },
  trial:    { color: "#0891b2", bg: "#ecfeff", label: "Trial" },
};

const AdminSubscriptions = () => {
  const [owners,   setOwners]   = useState([]);
  const [stats,    setStats]    = useState({});
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all");
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (search.trim()) params.set("search", search.trim());
      const res = await fetch(
        `${API_BASE_URL}/api/subscription/admin/owners?${params}`,
        { headers: adminAuthHeader() }
      );
      const data = await res.json();
      setOwners(data.owners || []);
      setStats(data.stats   || {});
    } catch (err) {
      console.error("Failed to load subscriptions:", err);
    }
    setLoading(false);
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  // ── Modal openers ────────────────────────────────────────────
  const openApprove = (owner) => {
    setModal({ type: "approve", owner });
    setMsg("");
    setForm({ plan: owner.subscription?.plan || "basic", amount: owner.subscription?.amount || 300, months: 12, note: "" });
  };

  const openSetExpiry = (owner) => {
    setModal({ type: "expiry", owner });
    setMsg("");
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    const existingExpiry = owner.subscription?.expiryDate
      ? new Date(owner.subscription.expiryDate).toISOString().split("T")[0]
      : nextYear.toISOString().split("T")[0];
    setForm({
      expiryDate: existingExpiry,
      plan: owner.subscription?.plan || "basic",
      amount: owner.subscription?.amount || 300,
      note: owner.subscription?.paymentNote || "",
    });
  };

  const openEdit = (owner) => {
    setModal({ type: "edit", owner });
    setMsg("");
    setForm({
      plan:       owner.subscription?.plan       || "basic",
      amount:     owner.subscription?.amount     || 300,
      expiryDate: owner.subscription?.expiryDate
        ? new Date(owner.subscription.expiryDate).toISOString().split("T")[0]
        : "",
      note: owner.subscription?.paymentNote || "",
    });
  };

  const openReject = (owner) => {
    setModal({ type: "reject", owner });
    setMsg("");
    setForm({ reason: "" });
  };

  const openDelete = (owner) => {
    setModal({ type: "delete", owner });
    setMsg("");
    setForm({});
  };

  // ── Submit handler ───────────────────────────────────────────
  const handleSubmit = async () => {
    setSaving(true);
    setMsg("");
    try {
      let url = "", body = {}, method = "PUT";

      if (modal.type === "approve") {
        url  = `${API_BASE_URL}/api/subscription/admin/approve/${modal.owner._id}`;
        body = form;
      } else if (modal.type === "expiry" || modal.type === "edit") {
        url  = `${API_BASE_URL}/api/subscription/admin/set-expiry/${modal.owner._id}`;
        body = form;
      } else if (modal.type === "reject") {
        url  = `${API_BASE_URL}/api/subscription/admin/reject/${modal.owner._id}`;
        body = { reason: form.reason };
      } else if (modal.type === "delete") {
        // Reset subscription to trial
        url  = `${API_BASE_URL}/api/subscription/admin/reject/${modal.owner._id}`;
        body = { reason: "Subscription reset by admin" };
      }

      const res  = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.ok) {
        setMsg("✅ " + data.message);
        setTimeout(() => { setModal(null); setMsg(""); load(); }, 1200);
      } else {
        setMsg("❌ " + (data.message || "Failed"));
      }
    } catch { setMsg("❌ Network error"); }
    setSaving(false);
  };

  const getDaysColor = (days) => {
    if (days === null || days === undefined) return "#6b7280";
    if (days <= 0)  return "#dc2626";
    if (days <= 7)  return "#dc2626";
    if (days <= 30) return "#d97706";
    return "#16a34a";
  };

  return (
    <AdminLayout>
      <div className="admin-header">
        <p className="admin-welcome">Manage Subscriptions</p>
        <h2 className="admin-title">Owner Subscriptions</h2>
      </div>

      {/* ── Stats Bar ── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { label: "Total",         value: stats.total,        color: "#4318FF", bg: "#ede9fe" },
          { label: "Active",        value: stats.active,       color: "#16a34a", bg: "#dcfce7" },
          { label: "Pending",       value: stats.pending,      color: "#d97706", bg: "#fef3c7" },
          { label: "Trial",         value: stats.trial,        color: "#0891b2", bg: "#ecfeff" },
          { label: "Expiring Soon", value: stats.expiringSoon, color: "#d97706", bg: "#fef3c7" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: "12px 18px", minWidth: 90, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value ?? 0}</div>
            <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Search + Filter ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200, display: "flex", gap: 8 }}>
          <input
            placeholder="Search name, email, city..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            style={{ flex: 1, padding: "9px 14px", borderRadius: 8, border: "1.5px solid #e0e5f2", fontSize: 13, outline: "none" }}
          />
          <button onClick={load}
            style={{ padding: "9px 18px", borderRadius: 8, background: "#4318FF", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            Search
          </button>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["all", "active", "pending", "trial"].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              style={{
                padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                background: filter === s ? "#4318FF" : "#f4f7fe",
                color: filter === s ? "#fff" : "#6b7280",
              }}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "#a3aed0", fontSize: 14 }}>Loading...</div>
        ) : owners.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#a3aed0", fontSize: 14 }}>No owners found</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f4f7fe" }}>
                  {["Owner", "Contact", "Status", "Expiry / Days Left", "Plan / Amount", "Payment Proof", "Actions"].map(h => (
                    <th key={h} style={{ padding: "12px 14px", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", borderBottom: "1px solid #e0e5f2" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {owners.map(o => {
                  const sc   = STATUS_CONFIG[o.subscription?.status] || STATUS_CONFIG.trial;
                  const days = o.daysRemaining;
                  return (
                    <tr key={o._id} style={{ borderBottom: "1px solid #f4f7fe" }}>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontWeight: 700, color: "#1b2559" }}>{o.name}</div>
                        <div style={{ fontSize: 11, color: "#a3aed0" }}>{o.city || "—"}</div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ color: "#374151" }}>{o.email}</div>
                        <div style={{ color: "#6b7280", fontSize: 12 }}>{o.phone || "—"}</div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ background: sc.bg, color: sc.color, padding: "4px 12px", borderRadius: 20, fontWeight: 700, fontSize: 12 }}>
                          {sc.label}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        {o.subscription?.expiryDate ? (
                          <>
                            <div style={{ fontSize: 12, color: "#374151" }}>
                              {new Date(o.subscription.expiryDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </div>
                            <div style={{ fontWeight: 700, color: getDaysColor(days), fontSize: 12, marginTop: 2 }}>
                              {days === null ? "—" : days <= 0 ? "Expired" : `${days} days left`}
                            </div>
                          </>
                        ) : <span style={{ color: "#a3aed0" }}>—</span>}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ color: "#374151", fontWeight: 600 }}>{o.subscription?.plan || "—"}</div>
                        <div style={{ color: "#6b7280", fontSize: 12 }}>
                          {o.subscription?.amount ? `₹${o.subscription.amount}` : "—"}
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        {o.subscription?.paymentProof ? (
                          <a href={o.subscription.paymentProof} target="_blank" rel="noreferrer"
                            style={{ color: "#4318FF", fontWeight: 600, fontSize: 12 }}>
                            View Screenshot
                          </a>
                        ) : <span style={{ color: "#a3aed0" }}>—</span>}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          <button onClick={() => openApprove(o)}   style={btn("#16a34a")}>✅ Approve</button>
                          <button onClick={() => openSetExpiry(o)} style={btn("#4318FF")}>📅 Set Expiry</button>
                          <button onClick={() => openEdit(o)}      style={btn("#0891b2")}>✏️ Edit</button>
                          {o.subscription?.status === "pending" && (
                            <button onClick={() => openReject(o)}  style={btn("#d97706")}>❌ Reject</button>
                          )}
                          <button onClick={() => openDelete(o)}    style={btn("#dc2626")}>🗑️ Reset</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      {modal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 440, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>

            <h3 style={{ margin: "0 0 6px", color: "#1b2559", fontSize: 16 }}>
              {modal.type === "approve" ? "✅ Approve Subscription" :
               modal.type === "expiry"  ? "📅 Set Expiry Date" :
               modal.type === "edit"    ? "✏️ Edit Subscription" :
               modal.type === "reject"  ? "❌ Reject Payment" :
                                          "🗑️ Reset Subscription"}
            </h3>
            <p style={{ color: "#6b7280", margin: "0 0 18px", fontSize: 13 }}>
              Owner: <b>{modal.owner.name}</b>
            </p>

            {modal.type === "delete" ? (
              <p style={{ color: "#dc2626", fontWeight: 600, fontSize: 13, background: "#fee2e2", padding: "12px 14px", borderRadius: 8 }}>
                This will reset the owner's subscription back to Trial status. Are you sure?
              </p>
            ) : modal.type === "reject" ? (
              <>
                <label style={lbl}>Rejection Reason</label>
                <input style={inp} placeholder="e.g. Payment screenshot unclear"
                  value={form.reason || ""} onChange={e => setForm({ ...form, reason: e.target.value })} />
              </>
            ) : (
              <>
                {(modal.type === "expiry" || modal.type === "edit") && (
                  <>
                    <label style={lbl}>Expiry Date *</label>
                    <input type="date" style={inp}
                      value={form.expiryDate || ""}
                      onChange={e => setForm({ ...form, expiryDate: e.target.value })} />
                  </>
                )}
                {modal.type === "approve" && (
                  <>
                    <label style={lbl}>Validity (months)</label>
                    <select style={inp} value={form.months || 12}
                      onChange={e => setForm({ ...form, months: parseInt(e.target.value) })}>
                      <option value={1}>1 month</option>
                      <option value={3}>3 months</option>
                      <option value={6}>6 months</option>
                      <option value={12}>12 months — 1 year</option>
                      <option value={24}>24 months — 2 years</option>
                    </select>
                  </>
                )}
                <label style={lbl}>Plan</label>
                <select style={inp} value={form.plan || "basic"}
                  onChange={e => setForm({ ...form, plan: e.target.value })}>
                  <option value="trial">Trial (free)</option>
                  <option value="basic">Basic — ₹300/year</option>
                  <option value="premium">Premium</option>
                </select>

                <label style={lbl}>Amount Collected (₹)</label>
                <input type="number" style={inp} placeholder="300"
                  value={form.amount || ""}
                  onChange={e => setForm({ ...form, amount: parseInt(e.target.value) })} />

                <label style={lbl}>Admin Note (optional)</label>
                <input style={inp} placeholder="e.g. Cash collected 20 Mar"
                  value={form.note || ""}
                  onChange={e => setForm({ ...form, note: e.target.value })} />
              </>
            )}

            {msg && (
              <div style={{
                marginTop: 10, padding: "10px 14px", borderRadius: 8, fontWeight: 600, fontSize: 13,
                background: msg.startsWith("✅") ? "#dcfce7" : "#fee2e2",
                color: msg.startsWith("✅") ? "#16a34a" : "#dc2626",
              }}>{msg}</div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              <button onClick={() => setModal(null)}
                style={{ padding: "9px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={saving}
                style={{
                  padding: "9px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff",
                  background: modal.type === "delete" || modal.type === "reject" ? "#dc2626" : "#4318FF",
                  opacity: saving ? 0.7 : 1,
                }}>
                {saving ? "Saving..." :
                 modal.type === "approve" ? "Approve" :
                 modal.type === "edit"    ? "Save Changes" :
                 modal.type === "expiry"  ? "Set Expiry" :
                 modal.type === "reject"  ? "Reject" : "Reset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const btn = (color) => ({
  padding: "5px 10px", borderRadius: 6, border: "none", cursor: "pointer",
  background: color, color: "#fff", fontWeight: 600, fontSize: 11, whiteSpace: "nowrap",
});

const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "#1b2559", marginBottom: 5 };
const inp = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1.5px solid #e0e5f2", marginBottom: 14, fontSize: 13,
  outline: "none", boxSizing: "border-box",
};

export default AdminSubscriptions;